import * as React from 'react';
import styled from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';

export interface InlineProps extends React.HTMLAttributes<HTMLDivElement> {
  gap?: string;
  align?: 'start' | 'center' | 'end' | 'baseline' | 'stretch';
  justify?: 'start' | 'center' | 'end' | 'between' | 'around';
  wrap?: boolean;
  semanticId?: string;
}

const alignMap: Record<NonNullable<InlineProps['align']>, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  baseline: 'baseline',
  stretch: 'stretch',
};

const justifyMap: Record<NonNullable<InlineProps['justify']>, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  between: 'space-between',
  around: 'space-around',
};

const shouldForward = (prop: string | number): boolean =>
  !['$gap', '$align', '$justify', '$wrap'].includes(String(prop));

const StyledInline = styled.div.withConfig({ shouldForwardProp: shouldForward })<{
  $gap: string;
  $align: string;
  $justify: string;
  $wrap: boolean;
}>`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  flex-wrap: ${({ $wrap }) => ($wrap ? 'wrap' : 'nowrap')};
  align-items: ${({ $align }) => $align};
  justify-content: ${({ $justify }) => $justify};
  gap: ${({ $gap }) => $gap};
`;

export const Inline = React.forwardRef<HTMLDivElement, InlineProps>(function Inline(
  {
    gap = `var(--ui-space-md, ${rem(16)})`,
    align = 'stretch',
    justify = 'start',
    wrap = false,
    semanticId,
    ...rest
  },
  ref,
) {
  const semanticProps = getSemanticAttributes(semanticId);

  return (
    <StyledInline
      ref={ref}
      $gap={gap}
      $align={alignMap[align]}
      $justify={justifyMap[justify]}
      $wrap={wrap}
      {...semanticProps}
      {...rest}
    />
  );
});

Inline.displayName = 'Inline';
